import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
  CartItem,
  updateCartItem,
  removeCartItem,
  getLimitStatus,
} from "../store/cart-store";
import { calculateShippingDate } from "../utils/knocking";
import { config as customizationConfig } from "../store/customization-store";

/**
 * <cart-item-row>
 *
 * Renders a single cart line item: image, title, meta, qty selector, price,
 * and remove button. Properties are set by the parent <cart-item-list> on
 * every cart update, so no internal signal subscription is needed.
 *
 * Quantity changes are optimistic + debounced:
 *  - _pendingQty drives the displayed value immediately on click
 *  - The API call fires 500 ms after the last click
 *  - _pendingQty is cleared after the API responds (only if no new
 *    interaction has started), at which point item.quantity takes over
 *
 * Properties:
 *   item  — CartItem object from cartData.items
 *   line  — 1-based line index used by the Shopify /cart/change.js API
 */
@customElement("cart-item-row")
export class CartItemRow extends LitElement {
  @property({ type: Object }) item!: CartItem;
  @property({ type: Number }) line!: number;
  @property({ type: Object }) customSkuItem: CartItem | null = null;
  @property({ type: Number }) customSkuLine = 0;

  /** Local quantity shown while a debounce is pending; null = use item.quantity */
  @state() private _pendingQty: number | null = null;
  @state() private _isRemoving = false;
  @state() private _showLimitNotice = false;

  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _limitNoticeTimer: ReturnType<typeof setTimeout> | null = null;
  private _limitType: "product" | "brand" | "global" | "none" = "none";

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._debounceTimer !== null) clearTimeout(this._debounceTimer);
    if (this._limitNoticeTimer !== null) clearTimeout(this._limitNoticeTimer);
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has("item") && this.item) {
      const status = getLimitStatus(this.item.product_id, this.item.vendor);
      this._limitType = status.type;
    }
  }

  private _triggerLimitNotice() {
    if (this._limitNoticeTimer !== null) clearTimeout(this._limitNoticeTimer);
    this._showLimitNotice = true;
    this._limitNoticeTimer = setTimeout(() => {
      this._showLimitNotice = false;
      this._limitNoticeTimer = null;
    }, 3000);
  }

  private get _displayQty(): number {
    return this._pendingQty ?? this.item.quantity;
  }

  /**
   * Fallback personalization fee, in cents, for products where a Shopify Cart
   * Transform Function merges the customization fee variant into this line
   * server-side (`has_components: true`) instead of it arriving as its own
   * cart line. The merged line only retains the "Your Text" property — the
   * fee variant's own id/price are dropped by the transform — so the amount
   * shown here comes from the configured customization cost rather than a
   * real line item. Returns null when a real companion line exists
   * (`customSkuItem`) or this item isn't a bundled personalization.
   */
  private get _bundledPersonalizationFeeCents(): number | null {
    if (this.customSkuItem) return null;
    if (!this.item.has_components) return null;
    if (!this.item.properties?.["Your Text"]) return null;
    return Math.round((customizationConfig.value.customSkuCost ?? 0) * 100);
  }

  private _formatPrice(cents: number): string {
    const currency =
      (window as any).__store?.cart?.cartData?.value?.currency ?? "USD";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(cents / 100);
  }

  /** Mirrors the old getItemMeta() logic — options + visible properties */
  private _getMeta(): string[] {
    const meta: string[] = [];

    for (const opt of this.item.options_with_values ?? []) {
      if (!opt.value.includes("Default Title")) {
        meta.push(`${opt.name}: ${opt.value}`);
      }
    }

    const props = this.item.properties ?? {};
    for (const [key, value] of Object.entries(props)) {
      // Skip internal properties (prefixed with _)
      if (key.charAt(0) === "_") continue;
      if (value) meta.push(`${key}: ${value}`);
    }

    return meta;
  }

  private _onMinus() {
    // Allow going to 0 — Shopify interprets qty=0 as remove
    this._scheduleSave(this._displayQty - 1);
  }

  private _onPlus() {
    const nextQty = this._displayQty + 1;
    const status = getLimitStatus(this.item.product_id, this.item.vendor);
    if (status.type !== "none" && nextQty > status.limit) {
      this._triggerLimitNotice();
      return;
    }
    this._scheduleSave(nextQty);
  }

  private _scheduleSave(qty: number) {
    this._pendingQty = qty;

    if (this._debounceTimer !== null) clearTimeout(this._debounceTimer);

    this._debounceTimer = setTimeout(async () => {
      this._debounceTimer = null;
      await updateCartItem(this.line, qty);
      // Keep custom SKU fee item quantity in sync
      if (this.customSkuLine > 0) {
        await updateCartItem(this.customSkuLine, qty);
      }
      // Only clear the pending display if no new interaction started
      // while the API call was in flight. If a new timer is running,
      // that interaction is now responsible for updating the display.
      if (this._debounceTimer === null) {
        this._pendingQty = null;
      }
    }, 500);
  }

  private async _onRemove() {
    if (this._debounceTimer !== null) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
    this._pendingQty = null;
    this._isRemoving = true;
    await removeCartItem(this.line);
    // Remove the associated custom-sku fee item if present
    if (this.customSkuLine > 0) {
      await removeCartItem(this.customSkuLine);
    }
  }

  /** "+ $X Personalization" banner — from a real companion line item when one
   * exists, otherwise from the bundled-line fallback. */
  private _renderPersonalizationBanner() {
    const feeCents = this.customSkuItem
      ? this.customSkuItem.final_price
      : this._bundledPersonalizationFeeCents;
    if (feeCents === null) return html``;

    return html`<div class="w-full flex justify-end">
      <div
        class="w-full flex justify-between items-center gap-1 rounded bg-[var(--color-secondary-100)] px-2 py-1 text-sm text-[var(--color-neutral-500)]"
      >
        <span class="font-bold text-[var(--color-secondary-800)]">Personalization</span>
        <span class="font-bold text-[var(--color-secondary-500)]"
          >+ ${this._formatPrice(feeCents)}</span
        >
      </div>
    </div>`;
  }

  render() {
    const rawImage = this.item.image?.startsWith("//")
      ? `https:${this.item.image}`
      : this.item.image;
    const imageUrl = rawImage
      ? rawImage.replace(/(\.[^.]*)$/, "_180x$1")
      : "";
    const meta = this._getMeta();
    const hasDiscount = this.item.final_price !== this.item.original_price;
    const qty = this._displayQty;

    const shipsByRaw = this.item.properties?.["_ships-by"];
    let shipsByBlock = html``;
    if (shipsByRaw) {
      try {
        const date = calculateShippingDate(shipsByRaw);
        const dateLine = (date.shortDate ?? date.longDate).toUpperCase();
        shipsByBlock = html`
          <div
            class="w-full rounded-sm rounded-t-none bg-[var(--color-primary-50)] p-1 flex flex-col items-center text-center"
            aria-label=${`Ships by ${date.longDate}`}
          >
            <span
              class="text-[10px] font-medium uppercase tracking-wide text-[var(--color-primary-950)]"
              >Ships by</span
            >
            <span
              class="text-xs font-bold uppercase leading-tight text-[var(--color-primary-950)]"
              >${dateLine}</span
            >
          </div>
        `;
      } catch {
        shipsByBlock = html``;
      }
    }

    return html`
      <div
        class="flex flex-wrap p-4 bg-white gap-x-4 rounded shadow-[0_3px_10px_rgba(0,0,0,0.04)] transition-all duration-300 relative${this._isRemoving ? " ajaxcart__row--removing" : ""}"
      >
        <div class="w-[95px] shrink-0 flex flex-col">
          <a href="${this.item.url}" tabindex="-1" class="flex shrink-0">
            <img
              src="${imageUrl}"
              alt="Image of ${this.item.product_title}"
              loading="lazy"
              class="w-[95px] h-[95px] object-contain border border-[var(--color-neutral-50)] rounded-sm rounded-b-none"
            />
          </a>
          ${shipsByBlock}
        </div>

        <div class="relative flex min-w-0 flex-1 flex-col gap-2.5">
          <button
            type="button"
            class="absolute top-0 right-0 z-[1] flex h-[25px] w-[25px] shrink-0 items-center justify-center rounded-full bg-[var(--color-neutral-100)] text-[var(--color-neutral-600)] transition-colors hover:text-[var(--color-neutral-700)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary-500)]"
            aria-label=${`Remove ${this.item.product_title}`}
            @click=${this._onRemove}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="h-4 w-4"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.644 51.644 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
              />
            </svg>
          </button>

          <div class="flex flex-wrap gap-y-2 pr-11">
            <a href="${this.item.url}" class="font-bold text-sm block w-full">
              <div class="text-caption text-[10px]">${this.item.vendor}</div>
              ${this.item.product_title}
            </a>
            ${meta.map(
              (m) =>
                html`<span class="variant-label small mr-[5px] text-left">${m}</span>`
            )}
          </div>

          <div
            class="relative overflow-hidden flex flex-wrap w-full items-center justify-between gap-y-2"
          >
            <div
              class="max-w-[120px] flex flex-col rounded py-1 px-2 bg-[var(--color-neutral-50)] justify-center"
            >
              ${this.item.properties?.["_hideQtySelector"]
                ? html`<span
                    class="text-sm font-bold text-center text-[var(--color-neutral-700)]"
                    >Qty: ${qty}</span
                  >`
                : html`<quantity-selector-cart
                    .quantity=${qty}
                    @quantity-minus=${this._onMinus}
                    @quantity-plus=${this._onPlus}
                  ></quantity-selector-cart>`}
            </div>
            <div class="flex flex-col items-end">
              ${hasDiscount
                ? html`
                    <div class="line-through text-[var(--color-base-text)]">
                      <strike
                        >${this._formatPrice(this.item.original_price)}</strike
                      >
                    </div>
                  `
                : ""}
              <div class="font-bold text-[var(--color-secondary-500)]">
                ${this._formatPrice(this.item.final_price)}
              </div>
            </div>
            ${this._renderPersonalizationBanner()}
          </div>
        </div>

        <div
          class="w-full basis-full grid transition-[grid-template-rows] duration-300 ease-out ${this._showLimitNotice ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}"
          aria-hidden=${String(!this._showLimitNotice)}
        >
          <div class="min-h-0 overflow-hidden">
            <div
              class="relative mt-4 flex min-h-[2rem] w-full items-center justify-center rounded-sm bg-[var(--color-danger-200)] px-2 py-1 transition-opacity duration-300 ease-out ${this._showLimitNotice ? "opacity-100" : "opacity-0"}"
            >
              <span
                class="text-center text-[11px] leading-tight text-[var(--color-danger-950)]"
                >You've reached the limit for this
                ${this._limitType === "brand" ? "brand" : "product"}</span
              >
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "cart-item-row": CartItemRow;
  }
}
